import { useState } from 'react';
import type { FarmingActivity } from '../page';

interface ActivityCalendarProps {
  activities: FarmingActivity[];
  onEdit: (activity: FarmingActivity) => void;
}

export default function ActivityCalendar({ activities, onEdit }: ActivityCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const getActivitiesForDate = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return activities.filter(activity => activity.scheduledDate === dateStr);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Scheduled': return 'bg-blue-500';
      case 'In Progress': return 'bg-orange-500';
      case 'Completed': return 'bg-green-500';
      case 'Delayed': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(<div key={`empty-${i}`} className="min-h-32 bg-gray-50"></div>);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dayActivities = getActivitiesForDate(day);
    const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();

    days.push(
      <div
        key={day}
        className={`min-h-32 border border-gray-200 p-2 ${isToday ? 'bg-teal-50 border-teal-300' : 'bg-white'}`}
      >
        <div className={`text-sm font-semibold mb-2 ${isToday ? 'text-teal-600' : 'text-gray-700'}`}>
          {day}
        </div>
        <div className="space-y-1">
          {dayActivities.slice(0, 3).map((activity) => (
            <button
              key={activity.id}
              onClick={() => onEdit(activity)}
              className="w-full text-left px-2 py-1 rounded text-xs hover:opacity-80 transition-opacity cursor-pointer"
              style={{ backgroundColor: getStatusColor(activity.status) + '20' }}
            >
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${getStatusColor(activity.status)}`}></div>
                <span className="text-gray-900 font-medium truncate">{activity.activityName}</span>
              </div>
              <div className="text-gray-600 text-xs truncate">{activity.assignedTo}</div>
            </button>
          ))}
          {dayActivities.length > 3 && (
            <div className="text-xs text-gray-500 px-2">
              +{dayActivities.length - 3} more
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {monthNames[month]} {year}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={previousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl text-gray-600 w-5 h-5 flex items-center justify-center"></i>
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Today
            </button>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl text-gray-600 w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>
        </div>

        <div className="flex gap-4 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-xs text-gray-600">Scheduled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span className="text-xs text-gray-600">In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-xs text-gray-600">Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-xs text-gray-600">Delayed</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-7">
        {dayNames.map((dayName) => (
          <div
            key={dayName}
            className="p-4 text-center text-sm font-semibold text-gray-700 bg-gray-50 border-b border-gray-200"
          >
            {dayName}
          </div>
        ))}
        {days}
      </div>
    </div>
  );
}
